import z from 'zod';

import {
  EntityBaseSchema,
  IdSchema,
  NonEmptyStringSchema,
  PythonicNameSchema,
} from '../../base';
import {
  AttributeReferencesSchema,
  BaseAttributeSchema,
  BaseVariantSchema,
} from './variant.schema';

const VariantIdSchema = EntityBaseSchema;

const AttributeIdSchema = EntityBaseSchema;

const VariantAndValuesSchema = BaseVariantSchema.extend({
  attributeValues: z.object({
    values: z
      .record(z.string(), z.string())
      .describe('Map of attribute ID to its value for this variant.'),
  }),
});

const AttributeAndValuesSchema = BaseAttributeSchema.extend({
  variantValues: z.object({
    values: z
      .record(z.string(), z.string())
      .describe(
        'Map of variant ID to the value of this attribute for that variant.',
      ),
  }),
});

export const CreateVariantSchema = VariantIdSchema.merge(
  VariantAndValuesSchema,
).describe('Create a new deployment variant with its attribute values.');

export type CreateVariantPayload = z.infer<typeof CreateVariantSchema>;

export const CreateAttributeSchema = AttributeIdSchema.merge(
  AttributeAndValuesSchema,
).describe(
  'Create a new variant attribute. Attributes hold per-variant values referenced in prompts via {{attr:name}} syntax.',
);

export type CreateAttributePayload = z.infer<typeof CreateAttributeSchema>;

export const DeleteAttributeSchema = AttributeIdSchema.describe(
  'Delete a variant attribute by its ID.',
);

export type DeleteAttributePayload = z.infer<typeof DeleteAttributeSchema>;

export const DeleteVariantSchema = VariantIdSchema.describe(
  'Delete a deployment variant by its ID.',
);

export type DeleteVariantPayload = z.infer<typeof DeleteVariantSchema>;

export const SetDefaultVariantSchema = VariantIdSchema.describe(
  'Set a variant as the default. Exactly one variant must be marked as default.',
);

export type SetDefaultVariantPayload = z.infer<typeof SetDefaultVariantSchema>;

export const UpdateAttributeSchema = AttributeIdSchema.merge(
  AttributeAndValuesSchema.partial(),
).describe(
  "Update a variant attribute's name, references, or per-variant values.",
);

export type UpdateAttributePayload = z.infer<typeof UpdateAttributeSchema>;

export const UpdateVariantSchema = VariantIdSchema.merge(
  VariantAndValuesSchema.partial(),
).describe("Update a variant's name or attribute values.");

export type UpdateVariantPayload = z.infer<typeof UpdateVariantSchema>;

// import related schemas
export const ImportVariantAttributeValuesSchema = z.object({
  variantId: IdSchema,
  attributeId: IdSchema,
  value: z.string(),
});

export type ImportVariantAttributeValuesPayload = z.infer<
  typeof ImportVariantAttributeValuesSchema
>;

export const ImportVariantSchema = z.object({
  id: IdSchema,
  name: NonEmptyStringSchema,
  isDefault: z
    .boolean()
    .describe(
      'Whether this is the default variant. Exactly one variant must be default.',
    ),
});

export type ImportVariantPayload = z.infer<typeof ImportVariantSchema>;

export const ImportAttributeSchema = z.object({
  id: IdSchema,
  name: PythonicNameSchema,
  references: AttributeReferencesSchema,
});

export type ImportAttributePayload = z.infer<typeof ImportAttributeSchema>;

export const VariantImportVariantsSchema = z
  .object({
    variants: z.array(ImportVariantSchema),
    attributes: z.array(ImportAttributeSchema),
    variantAttributeValues: z.array(ImportVariantAttributeValuesSchema),
    deletedVariantIds: z.array(IdSchema),
    deletedAttributeIds: z.array(IdSchema),
  })
  .describe(
    'Bulk import of variants, attributes, and their values. Includes IDs of variants and attributes to delete.',
  );

export type VariantImportVariantsPayload = z.infer<
  typeof VariantImportVariantsSchema
>;
